import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { InstanceKind } from '../worker/protocol';
import { facadeArray, facadeMaterial, windowMaterial } from './facadeStyles';
import { awningGeometry, roofBoxGeometry, signGeometry, signMaterial, windowTrimGeometry } from './buildingDetails';
import { LOWRISE } from '../gen/config';
import { carBodyGeometry } from './vehicleModels';
import { carPaintMaterial } from './models/materials';
import type { ModelLibrary } from './models/library';

/**
 * Instance kinds with per-instance data in instanceColor: window shader data (style, seed, kind), the
 * sign's atlas index, or a plain colour (roof boxes, awnings).
 */
export const WINDOW_KINDS = new Set<InstanceKind>(['towerWindow', 'lowWindow', 'roofBox', 'sign', 'awning', 'car0', 'car1', 'car2']);

function plane(offsetZ: number): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(1, 1);
  g.translate(0.5, 0.5, offsetZ);
  return g;
}

function colored(g: THREE.BufferGeometry, color: THREE.Color): THREE.BufferGeometry {
  const n = g.getAttribute('position').count;
  const cols = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    cols[i * 3] = color.r;
    cols[i * 3 + 1] = color.g;
    cols[i * 3 + 2] = color.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(cols, 3));
  return g;
}

function lampGeometry(): THREE.BufferGeometry {
  const pole = new THREE.CylinderGeometry(0.07, 0.1, 6, 6).translate(0, 3, 0).toNonIndexed();
  const arm = new THREE.BoxGeometry(0.08, 0.08, 1.2).translate(0, 5.95, 0.55).toNonIndexed();
  const head = new THREE.BoxGeometry(0.3, 0.15, 0.5).translate(0, 5.9, 1.1).toNonIndexed();
  const dark = new THREE.Color(0x2c2f33);
  const light = new THREE.Color(0xfff1c9);
  return mergeGeometries([colored(pole, dark), colored(arm, dark), colored(head, light)])!;
}

function treeGeometry(): THREE.BufferGeometry {
  const trunk = new THREE.CylinderGeometry(0.15, 0.22, 3, 6).translate(0, 1.5, 0).toNonIndexed();
  const crown = new THREE.IcosahedronGeometry(2.2, 0).translate(0, 4.2, 0).toNonIndexed();
  return mergeGeometries([colored(trunk, new THREE.Color(0x5b4632)), colored(crown, new THREE.Color(0x3f6f35))])!;
}

export class CityAssets {
  readonly facade: THREE.MeshStandardMaterial;
  readonly solid = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0 });
  readonly geometries: Record<InstanceKind, THREE.BufferGeometry>;
  readonly materials: Record<InstanceKind, THREE.Material>;

  private readonly facadeTex = facadeArray();

  constructor() {
    this.facade = facadeMaterial(this.facadeTex);
    const carPaint = carPaintMaterial();
    const treeMat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 });
    this.geometries = {
      towerWindow: plane(0.04),
      lowWindow: plane(0.04),
      door: plane(0.03),
      lamp: lampGeometry(),
      tree0: treeGeometry(),
      tree1: treeGeometry(),
      tree2: treeGeometry(),
      windowTrim: windowTrimGeometry(LOWRISE.windowWidth / 100, (LOWRISE.windowTop - LOWRISE.windowBottom) / 100),
      roofBox: roofBoxGeometry(),
      sign: signGeometry(),
      awning: awningGeometry(),
      car0: carBodyGeometry(0),
      car1: carBodyGeometry(1),
      car2: carBodyGeometry(2),
    };
    this.materials = {
      towerWindow: windowMaterial(),
      lowWindow: windowMaterial(0.5),
      door: new THREE.MeshStandardMaterial({ color: 0x5a3b25, roughness: 0.7 }),
      lamp: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.3 }),
      tree0: treeMat,
      tree1: treeMat,
      tree2: treeMat,
      windowTrim: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8 }),
      roofBox: new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0.2 }),
      sign: signMaterial(),
      awning: new THREE.MeshStandardMaterial({ roughness: 0.9, side: THREE.DoubleSide }),
      car0: carPaint,
      car1: carPaint,
      car2: carPaint,
    };
  }

  /**
   * The facade with its glass turned into tinted, partly transparent glass between world heights yMin
   * and yMax, so a tower's generated interior floors show through its windows (facadeStyles.ts).
   * Every instance shares one shader program.
   */
  /**
   * Level of detail per instance kind once the models are in: the model near the camera, the simple
   * built-in geometry further out (TileManager.applyLod).
   */
  readonly lod = new Map<InstanceKind, { high: { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[] }; low: { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[] } }>();

  /** Use the loaded models for cars and trees (register their new materials with `setup`). */
  useModels(lib: ModelLibrary, setup: (m: THREE.Material) => void): void {
    const byName = (n: string) => lib.cars.find((c) => c.name === n) ?? lib.cars[0];
    const cars: [InstanceKind, string][] = [
      ['car0', 'car-sedan'],
      ['car1', 'car-suv'],
      ['car2', 'car-sports'],
    ];
    for (const [k, n] of cars) {
      if (!lib.cars.length) break;
      this.lod.set(k, { high: { geometry: byName(n).geometry, material: this.materials[k] }, low: { geometry: this.geometries[k], material: this.materials[k] } });
    }
    const picks = [0, 2, 3].map((i) => lib.trees[Math.max(0, Math.min(lib.trees.length - 1, i))]);
    (['tree0', 'tree1', 'tree2'] as const).forEach((k, i) => {
      const t = picks[i];
      if (!t) return;
      t.materials.forEach(setup);
      this.lod.set(k, { high: { geometry: t.geometry, material: t.materials }, low: { geometry: this.geometries[k], material: this.materials[k] } });
    });
  }

  glassCutout(yMin: number, yMax: number): THREE.MeshStandardMaterial {
    const m = facadeMaterial(this.facadeTex, { value: new THREE.Vector2(yMin, yMax) });
    m.userData.owned = true;
    return m;
  }

  instanced(kind: InstanceKind, matrices: Float32Array, data?: Float32Array): THREE.InstancedMesh | null {
    const count = matrices.length / 16;
    if (count === 0) return null;
    const m = new THREE.InstancedMesh(this.geometries[kind], this.materials[kind], count);
    m.instanceMatrix = new THREE.InstancedBufferAttribute(matrices, 16);
    // Shader data rides in instanceColor (per mesh, unlike a geometry attribute on the shared geometry).
    if (data && data.length === count * 3 && WINDOW_KINDS.has(kind)) m.instanceColor = new THREE.InstancedBufferAttribute(data, 3);
    m.count = count;
    m.computeBoundingSphere();
    m.userData.sharedGeometry = true;
    m.name = kind;
    return m;
  }
}
